<script setup>
const title = "Ile kosztuje strona internetowa? Kalkulator cen stron www";
const description =
  "Sprawdź, ile kosztuje strona dla Twojej firmy. Wypełnij prosty kalkulator bez podawania danych – bez zobowiązań, bez stresu. Dowiedz się, ile zapłacisz, zanim podejmiesz decyzję.";

useSeoMeta({
  title,
  description,
});
/** Konfiguracja */
const supportRate = 0.2; // 20%/rok od jednorazowych
const domainCost = 250;
const serverCost = 250;
const sslCost = 100;

const marketingRef = ref(null);
const designRef = ref(null);
const sectionsRef = ref(null);
const sellingRef = ref(null);
const functionsRef = ref(null);
const additionalRef = ref(null);

/** Dodatkowe pozycje */

const oneTimeSubtotal = computed(() => {
  let total = 0;
  if (marketingRef?.value?.price) {
    total += marketingRef.value.price;
  }
  if (designRef?.value?.price) {
    total += designRef.value.price;
  }
  if (sectionsRef?.value?.price) {
    total += sectionsRef.value.price;
  }
  if (sellingRef?.value?.price) {
    total += sellingRef.value.price;
  }
  if (functionsRef?.value?.price) {
    total += functionsRef.value.price;
  }
  if (additionalRef?.value?.price) {
    total += additionalRef.value.price;
  }
  return total;
});

const pickedOptions = computed(() => {
  return {
    marketing: marketingRef?.value?.selectedTier,
    design: designRef?.value?.selectedTier,
    sections: sectionsRef?.value?.selectedItems,
    selling: sellingRef?.value?.count,
    functions: functionsRef?.value?.picked.map((f) => f.label),
    additional: additionalRef?.value?.picked.map((a) => a.label),
  };
});

const recurringSubtotal = computed(
  () =>
    domainCost + serverCost + sslCost + Math.round(oneTimeSubtotal.value * supportRate)
);

const supportFee = computed(() => Math.round(oneTimeSubtotal.value * supportRate));
const firstYearTotal = computed(() => oneTimeSubtotal.value + recurringSubtotal.value);

const showForm = ref(false);
const responseHtml = ref("");
function completed() {
  showForm.value = true;
}

const pickedOptionsRef = ref(null);
const summaryRef = ref(null);

const contactRef = ref(null);

function wrapEmail(fragment) {
  return `<!doctype html><html><body style="margin:0;background:#f6f6f6">
  <div style="max-width:640px;margin:auto;padding:24px">
      ${fragment}
  </div></body></html>`;
}

async function sendEmail(form) {
  const optionsHtml = pickedOptionsRef.value.getSummaryEmailHtml();
  const pricesHtml = summaryRef.value.getSummaryEmailHtml();

  const emailHtml = `
    <h3>Dane kontaktowe:</h3>
    <p>Imię: ${form.firstName}</p>
    <p>Intencja: ${form.intent}</p>
    <p>Email: ${form.email}</p>
    <p>Telefon: ${form.phone}</p>
    <p>O projekcie: ${form.about}</p>
  `;

  const fullHtml = emailHtml + optionsHtml + pricesHtml;
  const wrappedHtml = wrapEmail(fullHtml);

  try {
    const response = await $fetch("/api/mail", {
      method: "POST",
      body: {
        subject: "Nowa wiadomość z TurboWeb",
        html: wrappedHtml,
        replyToEmail: form.email,
        replyToName: form.firstName,
      },
    });

    contactRef.value.notice.kind = "";
    contactRef.value.notice.text = "Wysyłanie...";
  } catch (error) {
    contactRef.value.notice.kind = "error";
    contactRef.value.notice.text =
      "Wystąpił błąd podczas wysyłania wiadomości. Proszę spróbuj ponownie później.";
  } finally {
    contactRef.value.notice.text = "Udało się! Odezwimy się wkrótce.";
    contactRef.value.notice.kind = "success";
  }
}
</script>

<template>
  <div class="min-h-screen mx-auto max-w-7xl py-4 md:py-12">
    <PricesStepper
      @finish="completed"
      class="mt-12"
      :steps="[
        { key: 'intro', label: 'Wstęp' },
        { key: 'plan', label: 'Plan' },
        { key: 'design', label: 'Design' },
        { key: 'sekcje', label: 'Sekcje' },
        { key: 'selling', label: 'Przyciąganie uwagi' },
        { key: 'funkcje', label: 'Funkcje' },
        { key: 'dodatki', label: 'Dodatki' },
        { key: 'summary', label: 'Podsumowanie' },
      ]"
    >
      <template #intro>
        <PricesIntro />
      </template>

      <template #plan>
        <PricesMarketing ref="marketingRef" />
      </template>
      <template #design>
        <PricesDesign ref="designRef" />
      </template>

      <template #sekcje>
        <PricesSections ref="sectionsRef" />
      </template>

      <template #selling>
        <PricesSelling ref="sellingRef" />
      </template>

      <template #funkcje>
        <PricesFunctions ref="functionsRef" />
      </template>

      <template #dodatki>
        <PricesAdditional ref="additionalRef" />
      </template>

      <template #summary>
        <!-- RIGHT: live summary -->
        <div class="md:sticky md:top-6 h-fit">
          <PricesPickedOptions ref="pickedOptionsRef" :pkg="pickedOptions" />

          <PricesSummary
            class="mt-6"
            ref="summaryRef"
            :support-rate="supportRate"
            :one-time-subtotal="oneTimeSubtotal"
            :recurring-subtotal="recurringSubtotal"
            :support-fee="supportFee"
            :domain-cost="domainCost"
            :server-cost="serverCost"
            :ssl-cost="sslCost"
            :first-year-total="firstYearTotal"
          />

          <ContactModal ref="contactRef" @submitted="sendEmail" class="mt-6" />
        </div>
      </template>
    </PricesStepper>
  </div>
</template>
